import { useEffect, useMemo, useState } from 'react'
import type { OpenTab } from '@/components/features/document-tabs'
import { extractOpenDocumentIdsForSearch } from '@/lib/command-palette-ranking'
import { filterUuidIdsForSearch } from '@/lib/open-tab-ids-for-search'
import { fetchWithAuth } from '@/lib/api'

export type PrefetchedDocumentMeta = {
  id: string
  title: string
  type: string
  filePath?: string
}

type RecentContext = {
  ids: string[]
  documents: PrefetchedDocumentMeta[]
}

const RECENT_LIMIT = 20

let recentContextCache: RecentContext | null = null
let recentContextPromise: Promise<RecentContext> | null = null

async function loadRecentViewedDocumentContext(): Promise<RecentContext> {
  if (recentContextCache) return recentContextCache
  if (recentContextPromise) return recentContextPromise

  recentContextPromise = (async () => {
    try {
      const response = await fetchWithAuth(
        `/api/documents/recent?limit=${RECENT_LIMIT}&sortBy=viewed`,
      )
      if (!response.ok) {
        return { ids: [], documents: [] }
      }

      const data = await response.json()
      const documents: PrefetchedDocumentMeta[] = (data.documents ?? [])
        .map((doc: { id?: string; title?: string; type?: string; filePath?: string }) => ({
          id: doc.id ?? '',
          title: doc.title ?? '',
          type: doc.type ?? 'note',
          filePath: doc.filePath,
        }))
        .filter((doc: PrefetchedDocumentMeta) => doc.id.length > 0)

      const ids = filterUuidIdsForSearch(documents.map((doc) => doc.id)) ?? []
      const context = { ids, documents }
      recentContextCache = context
      return context
    } catch {
      return { ids: [], documents: [] }
    } finally {
      recentContextPromise = null
    }
  })()

  return recentContextPromise
}

/** Warm recent-view metadata once per session (deduped). Safe to call from chat mount. */
export async function prefetchDocumentSearchContext(_openTabIds: string[] = []): Promise<void> {
  await loadRecentViewedDocumentContext()
}

export function getPrefetchedDocumentMetadata(): PrefetchedDocumentMeta[] {
  return recentContextCache?.documents ?? []
}

/** Open tab UUIDs + recently viewed UUIDs for document search ranking boosts. */
export function useDocumentSearchBoostIds(openTabs: OpenTab[]) {
  const openTabIds = useMemo(() => extractOpenDocumentIdsForSearch(openTabs), [openTabs])
  const [recentlyViewedIds, setRecentlyViewedIds] = useState<string[]>(
    () => recentContextCache?.ids ?? [],
  )

  useEffect(() => {
    let cancelled = false

    loadRecentViewedDocumentContext().then(({ ids }) => {
      if (!cancelled) setRecentlyViewedIds(ids)
    })

    return () => {
      cancelled = true
    }
  }, [])

  return { openTabIds, recentlyViewedIds }
}
